import { useQuery } from '@tanstack/react-query';
import { StatusCode } from '@hungryroot/rest';
import {
  getApiV2DynamicConfigsName,
  type DynamicConfig as DynamicConfigInterface,
} from '@hungryroot/rest/api/v2/dynamic_configs/[name]/get';
import {
  getDynamicConfigs,
  type Params,
} from '@hungryroot/rest/api/v2/dynamic_configs/get';

const TIME_LIMIT_15_MINUTES = 15 * 60 * 1000;

export type { DynamicConfigInterface };

export enum DynamicConfigs {
  RolloverMinPointsPerOrder = 'rollover.min_points_per_order',
  CustomizeMarketingRecipesTitle3 = 'customize.marketing_recipes_title_3',
  CustomizeMarketingSkusTitle = 'customize.marketing_skus_title',
  CustomizeVolumeLimit = 'customize.volume_limit',
  CustomizeColdVolumeLimit = 'customize.cold_volume_limit',
  KidsSnacksGroceryNav = 'kids.enable_kids_grocery_nav',
  BestPrice = 'bpg.enable_bpg_grocery_nav',
  AntiInflammatory = 'anti_inflammatory.enable_anti_inflammatory_filter_nav',
  FunnelThirdPartySSODisabledUserAgents = 'funnel.third_party_sso_disabled_user_agents',
  InsufficientOrderThreshold = 'ssc.insufficient_order_threshold',
  AlgoliaClientKeyProduction = 'web.algolia_client_key.production',
  AlgoliaClientKeyStaging = 'web.algolia_client_key.staging',
  AlgoliaClientKeyQA = 'web.algolia_client_key.qa',
  AlgoliaClientKeyDevelopment = 'web.algolia_client_key.development',
  GroceriesCollections = 'api.scannable_entry_points.groceries',
  ScannableEntryPointsRecipes = 'api.scannable_entry_points.recipes',
}

enum REACT_QUERY_KEYS {
  DYNAMIC_CONFIGS = 'DynamicConfigs',
}

export enum DynamicConfigTypes {
  Customize = 'customize',
}

export type DynamicConfigParam = DynamicConfigs | DynamicConfigTypes;

const DYNAMIC_CONFIGS_PATH = '/api/v2/dynamic_configs/';

export function useDynamicConfigs(
  params: Params<(DynamicConfigs | DynamicConfigTypes)[]>,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ['dynamic_config', params],
    queryFn: async () => {
      const resp = await getDynamicConfigs(params);

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    staleTime: 5 * 60 * 1000,
  });
}

export function getCartSaveThresholdDynamicConfig(
  dynamicConfigs?: DynamicConfigInterface<DynamicConfigs | DynamicConfigTypes>[]
): number {
  if (dynamicConfigs === undefined) {
    return 0;
  }

  const match = dynamicConfigs.find(({ name }) => {
    return name === DynamicConfigs.RolloverMinPointsPerOrder;
  });

  if (match === undefined) {
    return 0;
  }

  return parseFloat(match.value);
}

export function useDynamicConfiguration(key: string) {
  return useQuery({
    queryKey: [REACT_QUERY_KEYS.DYNAMIC_CONFIGS, DYNAMIC_CONFIGS_PATH, key],
    queryFn: async () => {
      const resp = await getApiV2DynamicConfigsName({
        name: key,
      });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    gcTime: TIME_LIMIT_15_MINUTES,
    staleTime: TIME_LIMIT_15_MINUTES,
  });
}

export function getDynamicConfigValue(
  dynamicConfigs: DynamicConfigInterface<DynamicConfigs | DynamicConfigTypes>[],
  value: DynamicConfigs
) {
  const obj = dynamicConfigs.find((obj) => obj.name === value);

  return obj?.value;
}
